import React from "react";
import { MdError, MdOutlineBarChart } from "react-icons/md";

import { cx } from "@/lib/tailwind-utils";

// Loading component
const ChartLoading = ({ loadingClassName }: { loadingClassName?: string }) => (
  <div
    className={cx(
      "relative mt-4 h-full overflow-hidden rounded bg-background-secondary",
      loadingClassName,
    )}
  >
    <svg className="absolute inset-0 h-full w-full stroke-border" fill="none">
      <defs>
        <pattern
          id="pattern-1"
          x="0"
          y="0"
          width="10"
          height="10"
          patternUnits="userSpaceOnUse"
        >
          <path d="M-3 13 15-5M-5 5l18-18M-1 21 17 3"></path>
        </pattern>
      </defs>
      <rect
        stroke="none"
        fill="url(#pattern-1)"
        width="100%"
        height="100%"
      ></rect>
    </svg>
  </div>
);

// Define your no data component
const NoData = ({
  loadingClassName,
  missingDataMessage = "No data to show",
}: {
  missingDataMessage?: string;
  loadingClassName?: string;
}) => (
  <div
    className={cx(
      "mt-4 flex items-center justify-center rounded-md border border-border p-4",
      loadingClassName,
    )}
  >
    <div className="text-center">
      <MdOutlineBarChart
        className="mx-auto h-7 w-7 text-foreground-muted"
        aria-hidden={true}
      />
      <p className="mt-2 font-medium text-muted-foreground">
        {missingDataMessage}
      </p>
    </div>
  </div>
);

// Error component for failed data loading
const ErrorState = ({
  loadingClassName,
  errorMessage = "Failed to load data",
}: {
  errorMessage?: string;
  loadingClassName?: string;
}) => (
  <div
    className={cx(
      "flex min-h-full items-center justify-center py-8",
      loadingClassName,
    )}
  >
    <div className="text-center">
      <MdError
        className="mx-auto h-8 w-8 text-status-error-text"
        aria-hidden={true}
      />
      <p className="mt-3 font-medium text-status-error-text">{errorMessage}</p>
      <p className="mt-1 text-body text-status-error-text">
        Try refreshing to reload the data
      </p>
    </div>
  </div>
);

export default function ChartWrapper({
  children,
  loadingClassName,
  isDataLoaded,
  isLoading,
  hasFailed,
  errorMessage,
  loading,
}: {
  children: React.ReactElement;
  loadingClassName?: string;
  isDataLoaded: boolean;
  isLoading: boolean;
  hasFailed?: boolean;
  errorMessage?: string;
  // Optional custom loading node (e.g. a chart-shaped skeleton). Defaults to the
  // built-in hatch pattern so existing callers are unaffected.
  loading?: React.ReactNode;
}) {
  if (isLoading) {
    return loading ? (
      <>{loading}</>
    ) : (
      <ChartLoading loadingClassName={loadingClassName} />
    );
  }
  if (hasFailed) {
    return (
      <ErrorState
        loadingClassName={loadingClassName}
        errorMessage={errorMessage}
      />
    );
  }
  if (!isLoading && !isDataLoaded) {
    return <NoData loadingClassName={loadingClassName} />;
  }

  return children;
}
